import { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ApartmentOutlined as ApartmentOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  WarningAmberRounded as WarningIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import axios from "axios";
import { isEqual } from "lodash";
import { toast } from "react-toastify";
import { RootState } from "@/store";
import { Place, Option, ApiBuilding, ApiEntryPoint, StoredDefaultNote } from "@/types/common";
import { PHOTO_VIEWS } from "@/utils/constants";
import { getApiErrorMessage, orderPhotosByView } from "@/utils/helper";
import vr from "../../assets/vr.jpg";
import BuildingInfo from "./BuildingInfo";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { SuccessModal } from "@/components/feedback/successModel";

export interface EntryPointType {
  id: string;
  type: string;
  name?: string;
  photos?: (string | Blob | null)[];
  photoErrors?: boolean[];
  video?: string | Blob | null;
  videoError?: boolean;
  notesSelection?: Record<string, Option[]>;
  noteErrors?: Record<string, boolean>;
  tags?: string[];
  tagsError?: boolean;
}

export interface Building {
  id: string;
  name: string;
  entryPoints: EntryPointType[];
  exitPoints: EntryPointType[];
  hasSeparateExit: boolean;
}

const isEntryPointValid = (ep: EntryPointType) => {
  const hasPhotos = ep.photos && ep.photos.filter((p) => p !== null).length === 3;
  const hasNotes =
    (ep.notesSelection &&
      Object.values(ep.notesSelection).some(
        (arr: Option[]) => arr && arr.length > 0,
      )) ||
    (ep.tags && ep.tags.length > 0);
  return hasPhotos && hasNotes;
};

const isBuildingValid = (b: Building) => {
  const hasName = !!b.name && b.name.trim().length > 0;
  const hasEntry = b.entryPoints.length > 0;
  const hasExitIfRequired = !b.hasSeparateExit || b.exitPoints.length > 0;
  const allPoints = [...b.entryPoints, ...b.exitPoints];
  const allValid = allPoints.length > 0 && allPoints.every(isEntryPointValid);
  return hasName && hasEntry && hasExitIfRequired && allValid;
};

const flattenNotes = (notesSelection: Record<string, Option[]> | undefined) =>
  Object.values(notesSelection || {})
    .flat()
    .map((opt) => ({
      default_note_id: Number(opt.notes_data_id),
      note: opt.other_notes || opt.notes,
    }));

const styles = {
  container: {
    margin: "0 auto",
    backgroundColor: "#F8F8F8",
    minHeight: "100vh",
    paddingBottom: "80px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#fff",
    zIndex: 10,
    padding: "3% 4%",
    borderBottom: "1px solid #e0e0e0",
  },
  backButton: { marginRight: "8px", color: "#000" },
  title: { fontSize: "20px", fontWeight: 600, color: "#000" },
  mainContent: { padding: "4%" },
  placeCard: {
    borderRadius: "16px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
    marginBottom: "24px",
    backgroundColor: "#fff",
  },
  placeImage: { width: "100%", height: "120px", objectFit: "cover" as const },
  placeInfo: { padding: "3%" },
  placeName: { fontSize: "4.47vw", fontWeight: 700, color: "#191919", marginBottom: "8px" },
  addressRow: { display: "flex", alignItems: "flex-start", gap: "6px" },
  locationIcon: { fontSize: "4.47vw", color: "#9443DF", marginTop: "2px" },
  placeAddress: { fontSize: "2.82vw", color: "#626262" },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  sectionTitle: { fontSize: "4.24vw", fontWeight: 700, color: "#191919" },
  buildingCard: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
    marginBottom: "12px",
    cursor: "pointer",
  },
  buildingIconWrapper: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    backgroundColor: "#F0DEFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "16px",
  },
  buildingIcon: { color: "#6f2fa8" },
  buildingDetails: { flex: 1 },
  buildingName: { fontSize: "3.29vw", fontWeight: 600, color: "#0F172A", marginBottom: "2px" },
  buildingSubtext: { fontSize: "3.29vw", color: "#626262" },
  addBuildingButton: {
    width: "100%",
    padding: "12px",
    borderRadius: "30px",
    background: "#F0DEFF !important",
    color: "#8D28E6",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "3.29vw",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#e2c5ff", boxShadow: "none" },
  },
  bottomBar: {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "16px",
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    gap: "12px",
    zIndex: 1000,
  },
  draftButton: {
    flex: 1,
    textTransform: "none" as const,
    color: "#191919",
    fontWeight: 700,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    backgroundColor: "#F0DEFF",
    "&:hover": { backgroundColor: "#e2c5ff" },
  },
  submitButton: {
    flex: 1,
    textTransform: "none" as const,
    color: "#fff",
    fontWeight: 700,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    background: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    "&:hover": { backgroundColor: "#6034d6" },
  },
};

function mapStoredNotes(notes?: StoredDefaultNote[]): Record<string, Option[]> {
  const result: Record<string, Option[]> = {};
  for (const section of notes || []) {
    if (section.notes?.length) {
      result[section.header_text] = section.notes.map((n) => ({
        notes_data_id: String(n.default_notes_data_id),
        notes: n.note_text,
        other_notes: n.note_text,
      }));
    }
  }
  return result;
}

function mapEpToUI(ep: ApiEntryPoint, isRejectedRevision: boolean): EntryPointType {
  const rawPhotos = ep.media?.[0]?.photos || [];
  const orderedPhotos = orderPhotosByView(rawPhotos, PHOTO_VIEWS);
  const savedPhotos: (string | Blob | null)[] = orderedPhotos.map((p) => (p ? p.url : null));
  const photoErrors: boolean[] = orderedPhotos.map((p) => p ? (p.is_correct === false) : false);
  
  while (savedPhotos.length < 3) {
    savedPhotos.push(null);
    photoErrors.push(false);
  }

  const defaultNotes = ep.placeNotes?.[0]?.default_notes || [];
  const noteErrors: Record<string, boolean> = {};
  defaultNotes.forEach(n => {
    if (n.is_correct === false) {
      noteErrors[n.header_text] = true;
    }
  });

  return {
    id: String(ep.entry_point_id),
    type: ep.buildingSection?.section_type ?? ep.entry_name,
    name: ep.entry_name,
    photos: savedPhotos.slice(0, 3),
    photoErrors: isRejectedRevision ? photoErrors.slice(0, 3) : undefined,
    video: ep.media?.[0]?.video?.url ?? null,
    videoError: isRejectedRevision && (ep.media?.[0]?.video as unknown as {is_correct?: boolean})?.is_correct === false,
    notesSelection: mapStoredNotes(ep.placeNotes?.[0]?.default_notes),
    noteErrors: isRejectedRevision ? noteErrors : undefined,
    tags: ep.placeNotes?.[0]?.user_notes?.text
      ? ep.placeNotes[0].user_notes.text
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    tagsError: isRejectedRevision && (ep.placeNotes?.[0]?.user_notes as unknown as {is_correct?: boolean})?.is_correct === false,
  };
}

function mapApiBuildings(apiBuildings: ApiBuilding[], isRejectedRevision: boolean): Building[] {
  return [...apiBuildings]
    .sort((a, b) => a.order_index - b.order_index)
    .map((b, idx) => ({
      id: String(b.building_id ?? idx),
      name: b.building_name,
      hasSeparateExit: b.has_separate_exit,
      entryPoints: [...(b.entryPoints || [])]
        .filter((ep) => ep.buildingSection?.point_category === "entrance")
        .sort((a, b) => (a.buildingSection?.section_id ?? 0) - (b.buildingSection?.section_id ?? 0))
        .map(ep => mapEpToUI(ep, isRejectedRevision)),
      exitPoints: [...(b.entryPoints || [])]
        .filter((ep) => ep.buildingSection?.point_category === "exit")
        .sort((a, b) => (a.buildingSection?.section_id ?? 0) - (b.buildingSection?.section_id ?? 0))
        .map(ep => mapEpToUI(ep, isRejectedRevision)),
    }));
}

export default function ContributeNotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useApiClient();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { action, contribution_id } = useParams<{
    action?: string;
    contribution_id?: string;
  }>();
  const isUpdateMode = action === "update" && !!contribution_id;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);



  const newPlace = useMemo(() => {
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.setItem("contribute_place", JSON.stringify(location.state));
      return location.state;
    }
    const stored = sessionStorage.getItem("contribute_place");
    return stored ? JSON.parse(stored) : {};
  }, [location.state]);

  const place: Place =
    useSelector((state: RootState) => state.selectedPlace.place) || ({} as Place);
  const displayPlace = Object.keys(place).length > 0 ? place : newPlace;

  // Resolve place_id from all available sources — Redux place may have other keys
  // but no place_id when navigated without selecting a map pin first.
  const resolvedPlaceId =
    displayPlace?.place_id ?? place?.place_id ?? newPlace?.place_id ?? null;
  const placeName = displayPlace?.name || "Unknown Place";
  const placeAddress =
    displayPlace?.address_text || displayPlace?.formatted_address || "Address not available";

  const [buildings, setBuildings] = useState<Building[]>([
    { id: "1", name: "Building A", entryPoints: [], exitPoints: [], hasSeparateExit: false },
  ]);
  const [initialBuildings, setInitialBuildings] = useState<Building[] | null>(null);

  const isEdited = useMemo(() => {
    if (initialBuildings) {
      return !isEqual(buildings, initialBuildings);
    }
    return !isEqual(buildings, [
      { id: "1", name: "Building A", entryPoints: [], exitPoints: [], hasSeparateExit: false },
    ]);
  }, [buildings, initialBuildings]);

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };
  const [activeBuildingId, setActiveBuildingId] = useState<string | null>(null);
  const [buildingToDelete, setBuildingToDelete] = useState<Building | null>(null);
  const [isLongPressTriggered, setIsLongPressTriggered] = useState(false);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchMode = useRef(false);

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submittedContributionId, setSubmittedContributionId] = useState("");

  const locationPlaceId = (location.state as Record<string, unknown> | null)
    ?.place_id as string | number | undefined;

  const [isDraftLoading, setIsDraftLoading] = useState(
    isUpdateMode || !!locationPlaceId,
  );

  useEffect(() => {
    if (isUpdateMode && contribution_id) {
      setIsDraftLoading(true);
      api
        .get(ENDPOINTS.CONTRIBUTION.GET_BY_ID(contribution_id))
        .then((res) => {
          const contribution = res?.data?.data;
          if (contribution?.is_draft) {
            setIsEditingDraft(true);
          }
          if (contribution.buildings?.length) {
            const isRejectedRevision = contribution.status === "reviewed" && !contribution.is_best;
            const initial = mapApiBuildings(contribution.buildings, isRejectedRevision);
            setBuildings(initial);
            setInitialBuildings(initial);
          }
        })
        .catch((err) => {
          console.error("Failed to load draft:", err);
          toast.error(getApiErrorMessage(err, "Could not load draft data"));
        })
        .finally(() => setIsDraftLoading(false));
    } else if (locationPlaceId) {
      setIsDraftLoading(true);
      api
        .get(ENDPOINTS.CONTRIBUTION.DRAFT(String(locationPlaceId)))
        .then((res) => {
          const draft = res?.data?.data;
          if (draft?.buildings?.length) {
            const mapped = mapApiBuildings(draft.buildings, false);
            if (mapped.length > 0) {
              setBuildings(mapped);
              setInitialBuildings(mapped);
            }
          }
        })
        .catch(() => {
          // Handle error silently
        })
        .finally(() => setIsDraftLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTouchStart = (b: Building, e: React.TouchEvent | React.MouseEvent) => {
    if (e.type === "touchstart") isTouchMode.current = true;
    if (e.type === "mousedown" && isTouchMode.current) return;
    setIsLongPressTriggered(false);
    longPressTimer.current = setTimeout(() => {
      setIsLongPressTriggered(true);
      setBuildingToDelete(b);
    }, 500);
  };

  const handleTouchEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (e.type === "mouseup" && isTouchMode.current) return;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleClickBuilding = (id: string) => {
    if (isTouchMode.current) setTimeout(() => { isTouchMode.current = false; }, 100);
    if (!isLongPressTriggered) setActiveBuildingId(id);
  };

  const confirmDeleteBuilding = () => {
    if (buildingToDelete) {
      if (buildings.length === 1) {
        toast.error("A contribution must contain at least one building.");
      } else {
        setBuildings(buildings.filter((b) => b.id !== buildingToDelete.id));
        if (isUpdateMode) toast.success("Building deleted successfully");
      }
    }
    setBuildingToDelete(null);
  };

  const handleUpdateBuilding = (id: string, updates: Partial<Building>) => {
    setBuildings(buildings.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const handleAddBuilding = () => {
    const nextChar = String.fromCharCode(65 + buildings.length);
    setBuildings([
      ...buildings,
      {
        id: Date.now().toString(),
        name: `Building ${nextChar}`,
        entryPoints: [],
        exitPoints: [],
        hasSeparateExit: false,
      },
    ]);
  };

  const dataUrlToBlob = (dataUrl: string): Blob => {
    const [header, data] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)![1];
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  const uploadVideoBlob = async (
    blob: Blob,
    pointCategory: "entrance" | "exit",
  ): Promise<string> => {
    const ext = blob.type?.split("/")[1] || "mp4";
    const res = await api.get(ENDPOINTS.GENERATE_UPLOAD_URL, {
      folder: `${pointCategory}/videos`,
      filename: `contribution_video.${ext}`,
      contentType: blob.type || "video/mp4",
    });
    const { uploadUrl, blobUrl } = res.data;
    await fetch(uploadUrl, {
      method: "PUT",
      body: blob,
      headers: { "Content-Type": blob.type || "video/mp4" },
    });
    return blobUrl as string;
  };

  const resolveVideoBlobs = async (src: Building[]): Promise<Building[]> =>
    Promise.all(
      src.map(async (b) => ({
        ...b,
        entryPoints: await Promise.all(
          b.entryPoints.map(async (ep) => {
            if (ep.video instanceof Blob) {
              return { ...ep, video: await uploadVideoBlob(ep.video, "entrance") };
            }
            return ep;
          }),
        ),
        exitPoints: await Promise.all(
          b.exitPoints.map(async (ep) => {
            if (ep.video instanceof Blob) {
              return { ...ep, video: await uploadVideoBlob(ep.video, "exit") };
            }
            return ep;
          }),
        ),
      })),
    );

  const buildFormData = (
    isDraft: boolean,
    recaptchaToken: string,
    resolvedBuildings: Building[] = buildings,
  ): FormData => {
    const formData = new FormData();
    let epIndex = 0;

    const buildingsPayload = resolvedBuildings.map((building, bIdx) => {
      const allEntryPoints: object[] = [];
      const buildingDbId = isUpdateMode ? Number(building.id) || undefined : undefined;

      const processPoints = (
        points: EntryPointType[],
        pointCategory: "entrance" | "exit",
      ) => {
        points.forEach((ep) => {
          const currentIdx = epIndex++;

          const photoUrls: string[] = [];
          (ep.photos || []).forEach((photo, photoIdx) => {
            if (!photo) return;
            if (typeof photo === "string") {
              if (photo.startsWith("data:")) {
                const blob = dataUrlToBlob(photo);
                formData.append(
                  `ep_${currentIdx}_photos`,
                  blob,
                  `ep_${currentIdx}_photo_${PHOTO_VIEWS[photoIdx] ?? photoIdx}.jpg`,
                );
              } else {
                photoUrls.push(photo);
              }
            } else {
              formData.append(
                `ep_${currentIdx}_photos`,
                photo,
                `ep_${currentIdx}_photo_${PHOTO_VIEWS[photoIdx] ?? photoIdx}.jpg`,
              );
            }
          });

          const epDbId = isUpdateMode ? Number(ep.id) || undefined : undefined;

          allEntryPoints.push({
            ep_index: currentIdx,
            entry_point_id: epDbId,
            section_type: ep.type,
            point_category: pointCategory,
            entry_name: ep.name || ep.type,
            has_video: ep.video instanceof Blob,
            video_url: typeof ep.video === "string" ? ep.video : null,
            photo_urls: photoUrls,
            notes: {
              default_notes: flattenNotes(ep.notesSelection),
              user_notes: ep.tags?.length ? ep.tags.join(", ") : null,
            },
          });
        });
      };

      processPoints(building.entryPoints, "entrance");
      if (building.hasSeparateExit) {
        processPoints(building.exitPoints, "exit");
      }

      return {
        building_id: buildingDbId,
        building_name: building.name,
        has_separate_exit: building.hasSeparateExit,
        order_index: bIdx,
        entry_points: allEntryPoints,
      };
    });

    const newPlacePayload =
      !resolvedPlaceId && displayPlace?.g_place_id
        ? {
            name: displayPlace.name,
            address_text: displayPlace.address_text,
            coordinates: displayPlace.coordinates,
            country_code: displayPlace.country_code,
            city: displayPlace.city,
            postcode: displayPlace.postcode,
            subcategory_id: displayPlace.subcategory_id,
            g_place_id: displayPlace.g_place_id,
          }
        : undefined;

    formData.append(
      "data",
      JSON.stringify({
        place_id: resolvedPlaceId || null,
        is_draft: isDraft,
        buildings: buildingsPayload,
        ...(newPlacePayload ? { new_place: newPlacePayload } : {}),
      }),
    );

    formData.append("recaptchaToken", recaptchaToken);

    return formData;
  };

  const getRecaptchaToken = async (action: string): Promise<string> => {
    if (!executeRecaptcha) throw new Error("reCAPTCHA not ready");
    return executeRecaptcha(action);
  };

  const handleSubmit = async () => {
    const missingName = buildings.some((b) => !b.name || b.name.trim() === "");
    if (missingName) {
      setShowValidationErrors(true);
      toast.error("Building name is required.");
      return;
    }

    const missingEntryPoint = buildings.some((b) => b.entryPoints.length === 0);
    if (missingEntryPoint) {
      setShowValidationErrors(true);
      toast.error("Please add at least one entry point");
      return;
    }

    const missingExitPoint = buildings.some((b) => b.hasSeparateExit && b.exitPoints.length === 0);
    if (missingExitPoint) {
      setShowValidationErrors(true);
      toast.error("Please add at least one exit point");
      return;
    }

    const allValid = buildings.every(isBuildingValid);
    if (!allValid) {
      setShowValidationErrors(true);
      toast.error("Please complete all required fields in every building");
      return;
    }
    if (isUpdateMode && !isEditingDraft && initialBuildings && isEqual(initialBuildings, buildings)) {
      toast.info("No changes were made to update the contribution.");
      return;
    }

    try {
      setIsSubmitting(true);
      const [token, resolvedBuildings] = await Promise.all([
        getRecaptchaToken("contribute_submit"),
        resolveVideoBlobs(buildings),
      ]);
      if (isUpdateMode) {
        const res = await api.putFormData(
          ENDPOINTS.CONTRIBUTION.UPDATE(contribution_id!),
          buildFormData(false, token, resolvedBuildings),
        );
        setSubmittedContributionId(res?.data?.data?.contribution_id ?? contribution_id!);
      } else {
        const res = await api.postFormData(ENDPOINTS.CONTRIBUTE, buildFormData(false, token, resolvedBuildings));
        setSubmittedContributionId(res?.data?.data?.contribution_id ?? "");
      }
      setSuccessModalOpen(true);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        toast.error(getApiErrorMessage(error, "This contribution has already been moderated and can no longer be changed."));
      } else if (axios.isAxiosError(error) && error.response?.status === 423) {
        toast.error(getApiErrorMessage(error, "This contribution is currently under moderation. You cannot edit it right now."));
      } else {
        toast.error(getApiErrorMessage(error, "Failed to submit. Please try again."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraft = async () => {
    if (!isEdited && !isEditingDraft) {
      toast.info("No changes were made to save as a draft.");
      return;
    }

    const hasAnyContent = buildings.some(
      (b) => b.entryPoints.length > 0 || b.exitPoints.length > 0,
    );
    if (!hasAnyContent) {
      toast.info("Add at least one entry point before saving a draft");
      return;
    }

    try {
      setIsSavingDraft(true);
      const [token, resolvedBuildings] = await Promise.all([
        getRecaptchaToken("contribute_draft"),
        resolveVideoBlobs(buildings),
      ]);
      if (isUpdateMode) {
        await api.putFormData(
          ENDPOINTS.CONTRIBUTION.UPDATE(contribution_id!),
          buildFormData(true, token, resolvedBuildings),
        );
      } else {
        await api.postFormData(ENDPOINTS.CONTRIBUTE, buildFormData(true, token, resolvedBuildings));
      }
      toast.success("Draft saved successfully!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save draft. Please try again."));
    } finally {
      setIsSavingDraft(false);
    }
  };

  if (isDraftLoading) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Skeleton variant="circular" width={36} height={36} sx={{ mr: 1, flexShrink: 0 }} />
          <Skeleton variant="text" width={130} height={28} />
        </Box>

        <Box sx={{ padding: "4%" }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={120}
            sx={{ borderRadius: "16px", mb: "24px" }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: "12px" }}>
            <Skeleton variant="text" width={140} height={26} />
            <Skeleton variant="text" width={80} height={18} />
          </Box>

          {[1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width="100%"
              height={68}
              sx={{ borderRadius: "12px", mb: "12px" }}
            />
          ))}

          <Skeleton
            variant="rectangular"
            width="100%"
            height={46}
            sx={{ borderRadius: "30px", mt: "4px" }}
          />
        </Box>
      </Box>
    );
  }

  if (activeBuildingId) {
    const activeBuilding = buildings.find((b) => b.id === activeBuildingId);
    if (activeBuilding) {
      return (
        <Box>
          <BuildingInfo
            building={activeBuilding}
            onUpdateBuilding={handleUpdateBuilding}
            onBack={() => setActiveBuildingId(null)}
            initialShowErrors={showValidationErrors && !isBuildingValid(activeBuilding)}
            isEditingDraft={isEditingDraft}
          />
        </Box>
      );
    }
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton sx={styles.backButton} onClick={handleCancelClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={styles.title}>Contribute</Typography>
      </Box>

      <Box sx={styles.mainContent}>
        <Box sx={styles.placeCard}>
          <img src={vr} alt="Place" style={styles.placeImage} />
          <Box sx={styles.placeInfo}>
            <Typography sx={styles.placeName}>{placeName}</Typography>
            <Box sx={styles.addressRow}>
              <LocationOnOutlinedIcon sx={styles.locationIcon} />
              <Typography sx={styles.placeAddress}>{placeAddress}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.sectionHeader}>
          <Typography sx={styles.sectionTitle}>Building Blocks</Typography>
          <Typography sx={{ fontSize: "11px", color: "#999", fontStyle: "italic" }}>
            Long press to delete
          </Typography>
        </Box>

        {buildings.map((b) => {
          const entryCount = b.entryPoints.length;
          const exitCount = b.exitPoints.length;
          const entryText = `${entryCount} Entry Point${entryCount !== 1 ? "s" : ""}`;
          const exitText = `${exitCount} Exit Point${exitCount !== 1 ? "s" : ""}`;
          const hasError = showValidationErrors && !isBuildingValid(b);

          return (
            <Box
              key={b.id}
              sx={{
                ...styles.buildingCard,
                userSelect: "none",
                border: hasError ? "1px solid #FF4D4D" : "1px solid transparent",
              }}
              onClick={() => handleClickBuilding(b.id)}
              onMouseDown={(e) => handleTouchStart(b, e)}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              onTouchStart={(e) => handleTouchStart(b, e)}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchEnd}
              onContextMenu={(e) => e.preventDefault()}
            >
              <Box sx={styles.buildingIconWrapper}>
                <ApartmentOutlinedIcon sx={styles.buildingIcon} />
              </Box>
              <Box sx={styles.buildingDetails}>
                <Typography sx={styles.buildingName}>{b.name}</Typography>
                <Typography sx={styles.buildingSubtext}>
                  {entryCount === 0 && exitCount === 0
                    ? "No Entry or Exit points"
                    : `${entryText} • ${exitText}`}
                </Typography>
              </Box>

              {hasError && (
                <ErrorOutlineIcon sx={{ color: "#FF4D4D", mr: 1, fontSize: "20px" }} />
              )}

              <ChevronRightIcon sx={{ color: "#666" }} />
            </Box>
          );
        })}

        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          sx={styles.addBuildingButton}
          onClick={handleAddBuilding}
          disabled={isSubmitting || isSavingDraft}
        >
          Add Building
        </Button>
      </Box>

      <Box sx={styles.bottomBar}>
        {(() => {
          const showUpdateButtons = isUpdateMode && !isEditingDraft;
          return (
            <>
              <Button
                sx={styles.draftButton}
                onClick={showUpdateButtons ? handleCancelClick : handleDraft}
                disabled={isSubmitting || isSavingDraft}
              >
                {isSavingDraft ? (
                  <CircularProgress size={18} sx={{ color: "#191919" }} />
                ) : showUpdateButtons ? (
                  "Cancel"
                ) : (
                  "Draft"
                )}
              </Button>
              <Button
                sx={styles.submitButton}
                onClick={handleSubmit}
                disabled={isSubmitting || isSavingDraft}
              >
                {isSubmitting ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : showUpdateButtons ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
            </>
          );
        })()}
      </Box>

      <Dialog
        open={!!buildingToDelete}
        onClose={() => setBuildingToDelete(null)}
        PaperProps={{
          sx: {            
            borderRadius: "24px",
            padding: { xs: "4vw", sm: "16px" },
            maxWidth: "400px",
            width: "90%",
            textAlign: "center",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: "4vw", sm: 2 } }}>
          <Box
            sx={{
              backgroundColor: "#FFE5E5",
              borderRadius: "50%",
              width: { xs: "16vw", sm: 64 },
              height: { xs: "16vw", sm: 64 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningIcon sx={{ color: "#FF4D4D", fontSize: { xs: "8vw", sm: 32 } }} />
          </Box>
        </Box>
        
        {buildings.length === 1 ? (
          <>
            <Typography sx={{ fontSize: { xs: "4.5vw", sm: "18px" }, fontWeight: 700, color: "#000", mb: { xs: "6vw", sm: 3 } }}>
              Unable to delete this building
            </Typography>
            <Typography sx={{ fontSize: { xs: "3.5vw", sm: "14px" }, color: "#333", mb: { xs: "6vw", sm: 3 } }}>
              A contribution must contain at least one building. You cannot remove the only remaining building.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                fullWidth
                sx={{
                  backgroundColor: "#FF4D4D",
                  color: "#FFF",
                  borderRadius: "8px",
                  padding: { xs: "3vw", sm: "12px" },
                  fontWeight: 700,
                  fontSize: { xs: "3.5vw", sm: "14px" },
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#E60000" },
                }}
                onClick={() => setBuildingToDelete(null)}
              >
                Ok
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: { xs: "4.5vw", sm: "18px" }, fontWeight: 700, color: "#000", mb: { xs: "6vw", sm: 3 } }}>
              Do you want to delete {buildingToDelete?.name}?
            </Typography>
            {((buildingToDelete?.entryPoints.length || 0) > 0 || (buildingToDelete?.exitPoints.length || 0) > 0) && (
              <Box
                sx={{
                  backgroundColor: "#F8F8F8",
                  borderRadius: { xs: "3vw", sm: "12px" },
                  padding: { xs: "4vw", sm: "16px" },
                  textAlign: "left",
                  mb: { xs: "6vw", sm: 3 },
                }}
              >
                <Typography sx={{ fontSize: { xs: "3.5vw", sm: "14px" }, color: "#333", mb: { xs: "4vw", sm: 2 } }}>
                  Removing this building will also remove the following information:
                </Typography>
                {(buildingToDelete?.entryPoints.length || 0) > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <LoginIcon sx={{ color: "#666", fontSize: { xs: "4vw", sm: 16 } }} />
                    <Typography sx={{ fontSize: { xs: "3.5vw", sm: "14px" }, color: "#000", fontWeight: 600 }}>
                      {buildingToDelete?.entryPoints.length || 0} Entry Point
                      {buildingToDelete?.entryPoints.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                )}
                {(buildingToDelete?.exitPoints.length || 0) > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LogoutIcon sx={{ color: "#666", fontSize: { xs: "4vw", sm: 16 } }} />
                    <Typography sx={{ fontSize: { xs: "3.5vw", sm: "14px" }, color: "#000", fontWeight: 600 }}>
                      {buildingToDelete?.exitPoints.length || 0} Exit Point
                      {buildingToDelete?.exitPoints.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: { xs: "4vw", sm: "16px" } }}>
              <Button
                fullWidth
                sx={{
                  backgroundColor: "#F9E4E4",
                  color: "#FF4D4D",
                  borderRadius: "8px",
                  padding: { xs: "3vw", sm: "12px" },
                  fontWeight: 700,
                  fontSize: { xs: "3.5vw", sm: "14px" },
                  textTransform: "none",
                }}
                onClick={() => setBuildingToDelete(null)}
              >
                No
              </Button>
              <Button
                fullWidth
                sx={{
                  backgroundColor: "#FF4D4D",
                  color: "#FFF",
                  borderRadius: { xs: "2vw", sm: "8px" },
                  padding: { xs: "3vw", sm: "12px" },
                  fontWeight: 700,
                  fontSize: { xs: "3.5vw", sm: "14px" },
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#E60000" },
                }}
                onClick={confirmDeleteBuilding}
              >
                Yes
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            padding: "16px",
            maxWidth: "400px",
            width: "90%",
            textAlign: "center",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Box
            sx={{
              backgroundColor: "#FFE5E5",
              borderRadius: "50%",
              width: 64,
              height: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningIcon sx={{ color: "#FF4D4D", fontSize: 32 }} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#000", mb: 1 }}>
          {isEdited ? "Cancel Contribution?" : "Leave Contribution?"}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#666", mb: 3 }}>
          {isEdited 
            ? "Your progress will be lost if you cancel."
            : "Are you sure you want to leave this page?"}
        </Typography>
        <Box sx={{ display: "flex", gap: "16px" }}>
          <Button
            fullWidth
            sx={{
              backgroundColor: "#F8F8F8",
              color: "#191919",
              borderRadius: "8px",
              padding: "12px",
              fontWeight: 700,
              textTransform: "none",
            }}
            onClick={() => setShowCancelConfirm(false)}
          >
            Continue Editing
          </Button>
          <Button
            fullWidth
            sx={{
              backgroundColor: "#FF4D4D",
              color: "#FFF",
              borderRadius: "8px",
              padding: "12px",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: "#E60000" },
            }}
            onClick={() => {
              setShowCancelConfirm(false);
              navigate(-1);
            }}
          >
            Quit Anyway
          </Button>
        </Box>
      </Dialog>

      <SuccessModal
        contributionId={submittedContributionId}
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
      />
    </Box>
  );
}
